/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";
import { useDispatch } from "react-redux";

import {
  useShiftAssignments,
  usePostShiftAssignment,
  useDeleteShiftAssignment
} from "../../../../../hooks/query/useShiftAssignments";
import { ShiftCell } from "./ShiftCell";
import { UserMode } from "../../../../../interfaces/User";
import { useAppSelector } from "../../../../../store/hooks";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { notificationAdd } from "../../../../../actions/notification";
import { employeesSortedSelector } from "../../../../../reducers/employee";
import useCurrentResourceCentre from "../../../../../hooks/useCurrentResourceCentre";
import { serviceProvidersSortedSelector } from "../../../../../reducers/serviceProvider";

export const cellStyle = { border: 1, borderColor: "divider" };
const headerCellStyle = { ...cellStyle, fontWeight: 600, textAlign: "center" as const };

// eslint-disable-next-line import/prefer-default-export
export function ShiftAssignments(): React.ReactNode {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const shifts = rc?.shifts || [];

  // selectors
  const employees = useAppSelector(employeesSortedSelector);
  const serviceProviders = useAppSelector(serviceProvidersSortedSelector);

  const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf("week"));

  const fromDate = currentWeekStart.format("YYYY-MM-DD");
  const toDate = currentWeekStart.clone().endOf("week").format("YYYY-MM-DD");

  const { mutateAsync: postAssignment } = usePostShiftAssignment();
  const { mutateAsync: deleteAssignmentMutation } = useDeleteShiftAssignment();
  const { data: shiftAssignments = [], isLoading } = useShiftAssignments({ fromDate, toDate });

  const [deleteId, setDeleteId] = useState<number | null>(null);

  // variables
  const allStaff = useMemo(() => {
    const emps = employees
      .filter((e) => e.active)
      .map((e) => ({ ...e, authenticableType: UserMode.Employee }));
    const sps = serviceProviders
      .filter((sp) => sp.active)
      .map((sp) => ({
        ...sp,
        authenticableType: UserMode.ServiceProvider
      }));
    return [...emps, ...sps];
  }, [employees, serviceProviders]);

  const daysOfWeek = useMemo(() => {
    const days = [];
    const day = currentWeekStart.clone();
    for (let i = 0; i < 7; i += 1) {
      days.push({
        dateString: day.format("YYYY-MM-DD"),
        dayName: day.format("ddd"),
        dateDisplay: day.format("MMM D")
      });
      day.add(1, "day");
    }
    return days;
  }, [currentWeekStart]);

  const handlePrevWeek = () => {
    setCurrentWeekStart((prev) => prev.clone().subtract(1, "week"));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => prev.clone().add(1, "week"));
  };

  const handleAssignShift = async (
    shiftId: number,
    date: string,
    employeeId: number,
    authenticable: UserMode,
    defaultShiftId: number | null
  ) => {
    if (!rc?.id) return;

    if (Number(shiftId) === Number(defaultShiftId)) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "The selected shift is already the default shift for this user.",
          variant: "warning"
        })
      );
      return;
    }

    await postAssignment({
      resourceCentreId: rc.id,
      employeeId: authenticable === UserMode.Employee ? employeeId : undefined,
      serviceProviderId: authenticable === UserMode.ServiceProvider ? employeeId : undefined,
      shiftId,
      effectiveDate: date
    });
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteAssignmentMutation(deleteId);
      setDeleteId(null);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Weekly Shift Assignments</Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handlePrevWeek} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography fontWeight="500">
            {currentWeekStart.format("MMM D")} -{" "}
            {currentWeekStart.clone().endOf("week").format("MMM D, YYYY")}
          </Typography>
          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
          <Table
            size="small"
            sx={{ border: 1, borderColor: "divider", borderRadius: "5px", minWidth: 1000 }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellStyle, width: "200px" }}>Name</TableCell>
                {daysOfWeek.map((day) => (
                  <TableCell key={day.dateString} sx={{ ...headerCellStyle, minWidth: "140px" }}>
                    <Typography variant="subtitle2">{day.dayName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {day.dateDisplay}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allStaff.map((staff) => {
                const authenticable = staff.authenticableType;
                const name =
                  authenticable === UserMode.Employee
                    ? `${staff.firstName || ""} ${staff.lastName || ""}`.trim()
                    : `${(staff as any)?.title || ""} ${staff.firstName || ""} ${
                        staff.lastName || ""
                      }`.trim();

                return (
                  <TableRow key={`${authenticable}-${staff.id}`}>
                    <TableCell sx={cellStyle}>
                      <Typography variant="body2" fontWeight="500">
                        {name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ textTransform: "capitalize", color: "textSecondary" }}
                      >
                        ({authenticable === UserMode.Employee ? "Employee" : "Service Provider"})
                      </Typography>
                    </TableCell>

                    {daysOfWeek.map((day) => (
                      <TableCell
                        key={day.dateString}
                        sx={{ ...cellStyle, textAlign: "center", verticalAlign: "middle", p: 0 }}
                      >
                        <ShiftCell
                          dayDate={day.dateString}
                          allAssignments={shiftAssignments}
                          employeeId={staff.id}
                          authenticable={authenticable}
                          defaultShiftId={staff.defaultShiftId ?? null}
                          shifts={shifts}
                          defaultWeeklyOffs={(staff as any).defaultWeeklyOffs ?? []}
                          onAssignShift={(shiftId, date) =>
                            handleAssignShift(
                              shiftId,
                              date,
                              staff.id,
                              authenticable,
                              staff.defaultShiftId ?? null
                            )
                          }
                          onDeleteShift={(id) => setDeleteId(id)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}

              {allStaff.length === 0 && (
                <TableRow>
                  <TableCell colSpan={daysOfWeek.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">No staff found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {deleteId && (
        <OkhatiDialog
          title="Delete Shift Assignment"
          description="Are you sure you want to remove this shift assignment? This day will revert to the default shift if applicable."
          next={handleDelete}
          cancel={() => setDeleteId(null)}
          readMode={false}
        />
      )}
    </Box>
  );
}
